import React from "react";
import PropTypes from "prop-types";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Feedback from "react-bootstrap/Feedback";
import { clsx } from "clsx";
import { flatMap, snakeCase } from "lodash";
import { field } from "../";
import { ConditionalTooltip, Icon } from "../../components";

function getDefaultChecked(checked, defaultValue, defaultChecked) {
  if (checked !== undefined) {
    return undefined;
  }

  return defaultValue === undefined ? defaultChecked : defaultValue;
}

const FormCheckboxWidget = function FormCheckboxWidget(props) {
  const {
    label,
    labelIcon,
    labelClass,
    labelBold,
    help,
    helpImportant,
    error,
    defaultChecked = false,
    defaultValue,
    skipColon,
    singleColumn = true,
    marginBottom = 25,
    onChange = () => undefined,
    checkboxClassname,
    checked,
    "data-testid": dataTestId,
    tooltip = {},
    disabled = false,
    ...extra
  } = props;

  const filteredErrors =
    error && typeof error === "object" ? flatMap(error, value => value) : error;
  const hasErrors = filteredErrors?.length;

  const widgetClass = clsx(
    `checkbox-widget`,
    hasErrors && `has-error`,
    singleColumn && `checkbox-widget--single-column`,
    props.className,
    {
      hidden: props.hidden
    }
  );

  return (
    <Form.Group className={`${widgetClass} mb-${marginBottom}`}>
      <Row>
        <Col
          as={Form.Label}
          xs={3}
          className={clsx(
            labelBold && "form-label--bold",
            labelClass,
            disabled && "form-label--disabled"
          )}
          data-testid={`label_${snakeCase(dataTestId ?? label)}`}
        >
          {labelIcon && <Icon name={labelIcon} className="form-label-icon" />}
          {label && (skipColon ? label : <>{label}:</>)}
          <Feedback
            type="invalid"
            data-testid={`feedback_invalid_${snakeCase(dataTestId ?? label)}`}
          >
            {filteredErrors}
          </Feedback>
        </Col>
        <Col
          xs={9}
          className={clsx(
            "checkbox-widget__checkbox-column",
            checkboxClassname
          )}
        >
          <ConditionalTooltip
            condition={tooltip.show}
            tooltip={tooltip.message ?? ""}
            className={tooltip?.className ?? ""}
            wide={tooltip?.wide}
            placement="top"
          >
            <Form.Check
              checked={checked}
              type="checkbox"
              defaultChecked={getDefaultChecked(
                checked,
                defaultValue,
                defaultChecked
              )}
              className="new-checkbox"
              {...extra}
              onChange={onChange}
              isInvalid={hasErrors}
              data-testid={`checkbox_${snakeCase(dataTestId ?? label)}`}
              disabled={disabled}
            />
          </ConditionalTooltip>
        </Col>
      </Row>
      <Row>
        <Col xs={{ span: singleColumn ? 12 : 9, offset: singleColumn ? 0 : 3 }}>
          {help && (
            <Form.Text
              data-testid={`help_text_${snakeCase(dataTestId ?? label)}`}
            >
              {help}
            </Form.Text>
          )}
          {helpImportant && (
            <Form.Text
              className="form-text--important"
              data-testid={`help_text_important_${snakeCase(
                dataTestId ?? label
              )}`}
            >
              {helpImportant}
            </Form.Text>
          )}
        </Col>
      </Row>
    </Form.Group>
  );
};

FormCheckboxWidget.propTypes = {
  label: PropTypes.node.isRequired,
  labelIcon: PropTypes.string,
  labelBold: PropTypes.bool,
  help: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  error: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  defaultChecked: PropTypes.bool,
  data: PropTypes.bool,
  skipColon: PropTypes.bool,
  singleColumn: PropTypes.bool,
  marginBottom: PropTypes.oneOf([0, 5, 10, 15, 20, 25, 30]),
  checkboxClassname: PropTypes.string,
  checked: PropTypes.bool,
  "data-testid": PropTypes.string,
  tooltip: PropTypes.shape({
    message: PropTypes.string,
    show: PropTypes.bool,
    className: PropTypes.string,
    wide: PropTypes.bool
  }),
  disabled: PropTypes.bool
};

FormCheckboxWidget.propTypes.data = FormCheckboxWidget.propTypes.defaultChecked; // alias

export default field(FormCheckboxWidget);

export { FormCheckboxWidget };
